// ===========================================
// Kanban Board - Drag & Drop SDLC Board
// ===========================================
// Main kanban board container that handles drag & drop logic
// and organizes projects into SDLC columns
// Now supports dragging both cards AND columns for custom ordering
// User preferences (column order, collapsed state) persist to profile

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  rectIntersection,  // Added for better card-to-card collision detection
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import KanbanColumn from './KanbanColumn';
import KanbanCard from './KanbanCard';
import './KanbanBoard.scss';

// ===========================================
// SDLC Column Definitions
// ===========================================
// These represent the default stages a project progresses through
const DEFAULT_COLUMNS = [
  { 
    id: 'idea', 
    label: 'Idea', 
    color: 'var(--color-status-idea)',
    description: 'Concepts & brainstorming'
  },
  { 
    id: 'active', 
    label: 'Active', 
    color: 'var(--color-status-active)',
    description: 'In development'
  },
  { 
    id: 'paused', 
    label: 'Paused', 
    color: 'var(--color-status-paused)',
    description: 'On hold'
  },
  { 
    id: 'completed', 
    label: 'Completed', 
    color: 'var(--color-status-completed)',
    description: 'Shipped & live'
  },
  { 
    id: 'archived', 
    label: 'Archived', 
    color: 'var(--color-status-archived)',
    description: 'Retired projects'
  }
];

// ===========================================
// LocalStorage keys for fallback persistence
// ===========================================
// Primary storage is user profile; localStorage is fallback for offline/migration
const COLUMN_ORDER_KEY = 'devpad_kanban_column_order';
const COLLAPSED_COLUMNS_KEY = 'devpad_kanban_collapsed';

const KanbanBoard = ({ projects }) => {
  const { updateProject } = useProjects();
  const { user, updateProfile } = useAuth();
  
  // Ref to track if this is the initial mount (skip saving on first render)
  const isInitialMount = useRef(true);
  // Ref to store user preferences for stable callback reference
  const userPrefsRef = useRef(user?.preferences);
  
  // Keep the ref updated with latest user preferences
  useEffect(() => {
    userPrefsRef.current = user?.preferences;
  }, [user?.preferences]);
  
  // ===========================================
  // Column order state (persisted to user profile)
  // ===========================================
  // Priority: user preferences > localStorage fallback > default
  const [columns, setColumns] = useState(() => {
    // Check user profile preferences first
    const userOrder = user?.preferences?.kanban?.columnOrder;
    if (userOrder && userOrder.length > 0) {
      const orderedColumns = userOrder
        .map(id => DEFAULT_COLUMNS.find(col => col.id === id))
        .filter(Boolean);
      // Add any new columns not in saved order
      DEFAULT_COLUMNS.forEach(col => {
        if (!orderedColumns.find(c => c.id === col.id)) {
          orderedColumns.push(col);
        }
      });
      return orderedColumns;
    }
    
    // Fallback to localStorage for migration/offline
    const savedOrder = localStorage.getItem(COLUMN_ORDER_KEY);
    if (savedOrder) {
      try {
        const orderIds = JSON.parse(savedOrder);
        const orderedColumns = orderIds
          .map(id => DEFAULT_COLUMNS.find(col => col.id === id))
          .filter(Boolean);
        DEFAULT_COLUMNS.forEach(col => {
          if (!orderedColumns.find(c => c.id === col.id)) {
            orderedColumns.push(col);
          }
        });
        return orderedColumns;
      } catch {
        return DEFAULT_COLUMNS;
      }
    }
    return DEFAULT_COLUMNS;
  });

  // ===========================================
  // Collapsed columns state (persisted to user profile)
  // ===========================================
  // Tracks which columns the user has minimized
  const [collapsedColumns, setCollapsedColumns] = useState(() => {
    // Check user profile preferences first
    const userCollapsed = user?.preferences?.kanban?.collapsedColumns;
    if (userCollapsed && userCollapsed.length > 0) {
      return new Set(userCollapsed);
    }
    
    // Fallback to localStorage for migration/offline
    const localCollapsed = localStorage.getItem(COLLAPSED_COLUMNS_KEY);
    if (localCollapsed) {
      try {
        return new Set(JSON.parse(localCollapsed));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  // ===========================================
  // Debounced save to user profile
  // ===========================================
  // Saves preferences after 500ms of no changes to avoid excessive API calls
  const saveTimeoutRef = useRef(null);
  
  // Stable callback that uses refs to avoid dependency changes causing re-renders
  const savePreferences = useCallback((newColumnOrder, newCollapsedColumns) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save
    saveTimeoutRef.current = setTimeout(async () => {
      const orderIds = newColumnOrder.map(col => col.id);
      const collapsedIds = Array.from(newCollapsedColumns);
      
      // Save to localStorage as immediate backup
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(orderIds));
      localStorage.setItem(COLLAPSED_COLUMNS_KEY, JSON.stringify(collapsedIds));
      
      // Save to user profile (syncs across devices)
      // Use ref to get current preferences to avoid callback dependency issues
      try {
        await updateProfile({
          preferences: {
            ...userPrefsRef.current,
            kanban: {
              columnOrder: orderIds,
              collapsedColumns: collapsedIds
            }
          }
        });
      } catch (err) {
        // Silently fail - localStorage backup is already saved
        console.error('Failed to save kanban preferences:', err);
      }
    }, 500);
  }, [updateProfile]); // Only depends on updateProfile which is stable

  // Save when columns or collapsed state changes (but not on initial mount)
  useEffect(() => {
    // Skip the initial render to avoid unnecessary API call on page load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    savePreferences(columns, collapsedColumns);
  }, [columns, collapsedColumns, savePreferences]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // ===========================================
  // Toggle column collapse handler
  // ===========================================
  const handleToggleCollapse = useCallback((columnId) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  }, []);
  
  // Track currently dragging item for overlay
  const [activeProject, setActiveProject] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);

  // ===========================================
  // Setup drag sensors
  // ===========================================
  // PointerSensor for mouse/touch, KeyboardSensor for accessibility
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require some movement before starting drag (prevents accidental drags)
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // ===========================================
  // Group projects by status for each column
  // ===========================================
  const projectsByStatus = useMemo(() => {
    const grouped = {};
    
    // Initialize empty arrays for each column
    columns.forEach(col => {
      grouped[col.id] = [];
    });
    
    // Sort projects into their respective columns
    projects.forEach(project => {
      const status = project.status || 'idea';
      if (grouped[status]) {
        grouped[status].push(project);
      } else {
        // Fallback to idea if status is unknown
        grouped['idea'].push(project);
      }
    });
    
    // Sort each column by kanbanOrder (ascending - lower numbers first)
    // Falls back to lastFocused if kanbanOrder not set
    Object.keys(grouped).forEach(status => {
      grouped[status].sort((a, b) => {
        // If both have kanbanOrder, use that
        const orderA = a.kanbanOrder ?? Infinity;
        const orderB = b.kanbanOrder ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
        // Fallback to lastFocused (most recent first)
        return new Date(b.lastFocused) - new Date(a.lastFocused);
      });
    });
    
    return grouped;
  }, [projects, columns]);

  // Get column IDs for sortable context (prefixed to distinguish from card IDs)
  const columnIds = useMemo(() => columns.map(col => `column-${col.id}`), [columns]);

  // ===========================================
  // Custom collision detection
  // ===========================================
  // Prioritizes card-to-card collisions for reordering within columns,
  // then falls back to column detection for cross-column moves
  const collisionDetectionStrategy = (args) => {
    // If dragging a column, use closestCenter for horizontal reordering
    if (activeColumn) {
      return closestCenter(args);
    }
    
    // For cards: first try to find collisions with other cards using rectIntersection
    // This gives us better detection for vertical reordering within columns
    const rectCollisions = rectIntersection(args);
    
    // Filter to find card collisions (IDs that are NOT column IDs)
    // Column IDs start with 'column-' prefix OR match a column.id directly
    const cardCollisions = rectCollisions.filter(
      collision => !String(collision.id).startsWith('column-') && 
                   !columns.some(col => col.id === collision.id)
    );
    
    // If we found card collisions, return them (prioritize card-to-card for reordering)
    if (cardCollisions.length > 0) {
      return cardCollisions;
    }
    
    // Fall back to pointerWithin for column detection (cross-column moves)
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    
    // Final fallback to closestCenter
    return closestCenter(args);
  };

  // ===========================================
  // Helper to determine if an ID is a column
  // ===========================================
  const isColumnId = (id) => {
    return String(id).startsWith('column-');
  };

  // Extract the actual column ID from the sortable ID
  const getColumnIdFromSortableId = (sortableId) => {
    return String(sortableId).replace('column-', '');
  };

  // ===========================================
  // Drag Event Handlers
  // ===========================================
  
  // When drag starts, track the active item for the overlay
  const handleDragStart = (event) => {
    const { active } = event;
    
    // Check if we're dragging a column or a card
    if (isColumnId(active.id)) {
      // Dragging a column - set active column for overlay
      const columnId = getColumnIdFromSortableId(active.id);
      const draggedColumn = columns.find(col => col.id === columnId);
      setActiveColumn(draggedColumn);
      setActiveProject(null);
    } else {
      // Dragging a card - set active project for overlay
      const draggedProject = projects.find(
        p => (p._id || p.id) === active.id
      );
      setActiveProject(draggedProject);
      setActiveColumn(null);
    }
  };

  // When drag ends, handle column reorder or project status/order change
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    // Clear active items
    setActiveProject(null);
    setActiveColumn(null);
    
    // If no drop target, do nothing
    if (!over) return;
    
    // Don't do anything if dropped on itself
    if (active.id === over.id) return;
    
    // ===========================================
    // Handle Column Reordering
    // ===========================================
    if (isColumnId(active.id) && isColumnId(over.id)) {
      const activeColumnId = getColumnIdFromSortableId(active.id);
      const overColumnId = getColumnIdFromSortableId(over.id);
      
      // Only reorder if dropped on a different column
      if (activeColumnId !== overColumnId) {
        setColumns(cols => {
          const oldIndex = cols.findIndex(col => col.id === activeColumnId);
          const newIndex = cols.findIndex(col => col.id === overColumnId);
          // Use arrayMove from dnd-kit to reorder the array
          return arrayMove(cols, oldIndex, newIndex);
        });
      }
      return;
    }
    
    // ===========================================
    // Handle Project Card Movement
    // ===========================================
    if (!isColumnId(active.id)) {
      // Find the project that was dragged
      const draggedProject = projects.find(
        p => (p._id || p.id) === active.id
      );
      
      if (!draggedProject) return;
      
      // Determine the target column (status) and position
      let targetStatus = draggedProject.status;
      let targetProject = null;
      
      // Check if we dropped on a column header/area
      if (isColumnId(over.id)) {
        targetStatus = getColumnIdFromSortableId(over.id);
      } else {
        // Dropped on a project card - get that project's info
        targetProject = projects.find(p => (p._id || p.id) === over.id);
        if (targetProject) {
          targetStatus = targetProject.status;
        }
      }
      
      // Verify this is a valid status
      const isValidStatus = columns.some(col => col.id === targetStatus);
      if (!isValidStatus) return;
      
      // ===========================================
      // Same Column Reordering (dropped on another card in same column)
      // ===========================================
      if (draggedProject.status === targetStatus && targetProject && 
          (draggedProject._id || draggedProject.id) !== (targetProject._id || targetProject.id)) {
        // Get the current order of projects in this column
        const columnProjects = [...(projectsByStatus[targetStatus] || [])];
        const draggedId = draggedProject._id || draggedProject.id;
        const targetId = targetProject._id || targetProject.id;
        
        const oldIndex = columnProjects.findIndex(p => (p._id || p.id) === draggedId);
        const newIndex = columnProjects.findIndex(p => (p._id || p.id) === targetId);
        
        // Only reorder if indices are different and valid
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          // Create new order array
          const reorderedProjects = arrayMove(columnProjects, oldIndex, newIndex);
          
          // Calculate new kanbanOrder values - use index * 1000 for spacing
          const updates = reorderedProjects.map((project, index) => ({
            id: project._id || project.id,
            kanbanOrder: index * 1000
          }));
          
          // Update all projects with new kanbanOrder values
          for (const update of updates) {
            await updateProject(update.id, { kanbanOrder: update.kanbanOrder });
          }
        }
        return;
      }
      
      // ===========================================
      // Cross-Column Movement (status change)
      // ===========================================
      if (draggedProject.status !== targetStatus) {
        // Calculate kanbanOrder for the new position
        let newKanbanOrder;
        
        if (targetProject) {
          // Dropped on a specific card - insert at that position
          const targetColumnProjects = projectsByStatus[targetStatus] || [];
          const targetIndex = targetColumnProjects.findIndex(
            p => (p._id || p.id) === (targetProject._id || targetProject.id)
          );
          // Insert just before the target (use target's order - 1)
          newKanbanOrder = targetIndex * 1000;
        } else {
          // Dropped on column itself - add to end
          const targetColumnProjects = projectsByStatus[targetStatus] || [];
          newKanbanOrder = targetColumnProjects.length * 1000;
        }
        
        // Update the project status and order via context
        await updateProject(draggedProject._id || draggedProject.id, {
          status: targetStatus,
          kanbanOrder: newKanbanOrder
        });
      }
    }
  };

  // Handle drag cancellation
  const handleDragCancel = () => {
    setActiveProject(null);
    setActiveColumn(null);
  };

  return (
    <div className="kanban-board">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Sortable context for columns (horizontal reordering) */}
        <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
          <div className="kanban-board__columns">
            {columns.map(column => (
              <KanbanColumn
                key={column.id}
                column={column}
                projects={projectsByStatus[column.id] || []}
                isCollapsed={collapsedColumns.has(column.id)}
                onToggleCollapse={handleToggleCollapse}
              />
            ))}
          </div>
        </SortableContext>

        {/* Drag Overlay - Shows floating card or column while dragging */}
        <DragOverlay>
          {/* Show card overlay when dragging a project */}
          {activeProject && (
            <div className="kanban-card kanban-card--overlay">
              <KanbanCard project={activeProject} isDragging />
            </div>
          )}
          {/* Show column overlay when dragging a column */}
          {activeColumn && (
            <div className="kanban-column kanban-column--overlay">
              <div className="kanban-column__header">
                <div 
                  className="kanban-column__status-dot"
                  style={{ background: activeColumn.color }}
                />
                <h3 className="kanban-column__title">{activeColumn.label}</h3>
                <span className="kanban-column__count">
                  {(projectsByStatus[activeColumn.id] || []).length}
                </span>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanBoard;
